import { platform } from "os";
import client from "./client";

export const api = {
  login: (data: any) => {
    return client.post("/auth/login", {
      ...data,
      userType: "A",
    });
  },
  dashboard: (id: string) => {
    return client.get(`/users/${id}/dashboard`).then((res) => res.data);
  },
  getUser: ({ id }: { id: string }) => {
    return client.get(`/users/${id}`);
  },
  // /users/{id}/reports
  getUserRevenue: ({
    id,
    type,
    duration,
  }: {
    id: string;
    type: "USERS_COUNT" | "REVENUE";
    duration: "CURRENT_WEEK" | "CURRENT_MONTH" | "CURRENT_YEAR";
  }) => {
    return client
      .get(`/users/${id}/reports`, {
        params: {
          type,
          duration,
        },
      })
      .then((res) => res.data);
  },
  getOrganisations: ({
    page,
    limit,
    searchKeyword,
    platform,
    type,
    showActive = true,
    sortBy,
    sortOrder,
  }: {
    page?: number;
    limit?: number;
    searchKeyword?: string;
    platform?: string[];
    type?: string;
    showActive?: boolean;
    sortBy?: string;
    sortOrder?: string;
  }) => {
    let params = {
      page,
      limit,
    };

    params["showActive"] = showActive;
    if (searchKeyword) params["searchKeyword"] = searchKeyword;
    if (type) params["type"] = type;

    if (sortBy) {
      params["sortBy"] = sortBy;
    }
    if (sortOrder) {
      params["sortOrder"] = sortOrder;
    }
    if (platform && platform?.length > 0)
      params["platform"] = platform.join(",");

    // Serialize params to query string
    const queryString = Object.entries(params)
      .map(([key, value]: any) => `${key}=${encodeURIComponent(value)}`)
      .join("&");

    return client.get(`/organisations?${queryString}`).then((res) => res.data);
  },
  createOrganisation: (data: any) => {
    return client.post("/organisations", data);
  },
  updateOrganisation: (id: string, data: any) => {
    return client.patch(`/organisations/${id}`, data);
  },
  updateOrganizationStatus: (id: string, isActive: boolean) => {
    return client.patch(`/organisations/${id}/status`, {
      isActive,
    });
  },
  approveOrganization: (id: string) => {
    return client.patch(`/organisations/${id}/request/approve`);
  },
  declineOrganization: (id: string) => {
    return client.patch(`/organisations/${id}/request/decline`);
  },
  deleteOrganization: (id: string) => {
    return client.delete(`/organisations/${id}`);
  },
  deleteOrganizationRequest: (id: string) => {
    return client.delete(`/organisations/${id}/request`);
  },
  getUsers: (
    {
      page,
      limit,
      search,
      showActive = true,
      filters,
      type,
      sortBy,
      sortOrder,
    }: {
      page: number;
      limit: number;
      search?: string;
      showActive?: boolean;
      filters?: any;
      type?: string;
      sortBy?: string;
      sortOrder?: string;
    },
    signal?: any
  ) => {
    let url = `/users?page=${page}&limit=${limit}&showActive=${showActive}`;
    if (type) {
      url += `&type=${type}`;
    }
    if (search) {
      url += `&searchKeyword=${search}`;
    }
    if (filters?.date?.startDate && filters?.date?.endDate) {
      url += `&startDate=${filters?.date?.startDate}&endDate=${filters?.date?.endDate}`;
    }

    if (sortBy) {
      url += `&sortBy=${sortBy}`;
    }
    if (sortOrder) {
      url += `&sortOrder=${sortOrder}`;
    }
    if (filters?.types && filters?.types?.length > 0) {
      const types = filters.types.join(",");
      if (types?.length > 0) {
        url += `&tiers=${filters.types.join(",")}`;
      }
    }
    return client
      .get(url, {
        signal,
      })
      .then((res) => res.data);
  },
  updateUser: (id: string, isActive: boolean) => {
    return client.patch(`/users/${id}/status`, {
      isActive,
    });
  },
  updateUserInfo: (id: string, data: any) => {
    return client.put(`/users/${id}`, data);
  },
  deleteUser: (id: string) => {
    return client.delete(`/users/${id}`);
  },
  getPosts: (
    {
      page,
      limit,
      userId,
      type,
    }: {
      page: number;
      limit: number;
      userId: string;
      type?: string;
    },
    signal?: any
  ) => {
    console.log("fetching posts", userId);
    let url = `/posts?userId=${userId}&page=${page}&limit=${limit}`;
    return client
      .get(url, {
        signal,
      })
      .then((res) => res.data);
  },
  getLikedPosts: (
    {
      userId,
      page,
      limit,
    }: {
      userId: string;
      page: number;
      limit: number;
    },
    signal?: any
  ) => {
    let url = `/posts/fetch/liked-posts?userId=${userId}&page=${page}&limit=${limit}`;
    return client
      .get(url, {
        signal,
      })
      .then((res) => res.data);
  },
  getSavedPosts: (
    {
      userId,
      page,
      limit,
    }: {
      userId: string;
      page: number;
      limit: number;
    },
    signal?: any
  ) => {
    let url = `/posts/fetch/bookmarked-posts?userId=${userId}&page=${page}&limit=${limit}`;
    return client
      .get(url, {
        signal,
      })
      .then((res) => res.data);
  },
  deletePost: (id: string) => {
    return client.delete(`/posts/${id}`);
  },
  getStylists: (id: string) => {
    return client.get(`/users/${id}/get-stylists`).then((res) => res.data);
  },
  getReviews: (
    {
      page,
      limit,
      userId,
      othersReviews,
    }: {
      page: number;
      limit: number;
      userId: string;
      othersReviews: boolean;
    },
    signal?: any
  ) => {
    console.log("fetching posts", userId);
    let url = `/users/${userId}/reviews?page=${page}&limit=${limit}`;
    if (othersReviews) {
      url += `&othersReviews=true`;
    }
    return client
      .get(url, {
        signal,
      })
      .then((res) => res.data);
  },
  getServices: () => {
    return client.get("/users/services/get").then((res) => res.data);
  },
  createService: (data: any) => {
    return client.post("/users/services/add", data);
  },
  updateService: (data: any) => {
    return client.put(`/users/services/update/${data?.id}`, {
      name: data?.name,
    });
  },
  deleteService: (id: string) => {
    return client.delete(`/users/services/delete/${id}`);
  },
  getFileUpload: (data: {
    uploadType: string;
    fileName: string;
    mimeType: string;
    id: string;
  }) => {
    const query = `uploadType=${data.uploadType}&fileName=${data.fileName}&mimeType=${data.mimeType}`;
    return client
      .get(`/users/${data.id}/images/signed-url?${query}`)
      .then((res) => res.data);
  },
  createBanner: (data: any) => {
    return client.post("/users/banner/save", data);
  },
  updateBanner: (data: any) => {
    return client.put(`/users/banner/update/${data?.id}`, data);
  },
  deleteBanner: (id: string) => {
    return client.delete(`/users/banner/delete/${id}`);
  },
  getBanners: () => {
    return client.get("/users/banner/get").then((res) => res.data);
  },
};
